<template>
  <div class="public-payment-page">
    <main class="public-payment-page__shell">
      <v-card v-if="notFound" class="public-payment-page__state-card" outlined>
        <v-icon size="42" color="grey">mdi-link-variant-off</v-icon>
        <h1>This link has expired</h1>
        <p>Please contact the school and ask for a new payment collection link.</p>
      </v-card>

      <template v-else>
        <v-card v-if="!cardCollected" class="public-payment-page__card" outlined>
          <header v-if="Info" class="public-payment-page__header">
            <img
              v-if="Info.Business.imageUrl"
              class="public-payment-page__logo"
              :src="Info.Business.imageUrl"
              :alt="`${Info.Business.name} logo`"
            />
            <div v-else class="public-payment-page__logo-placeholder" aria-hidden="true">
              {{ businessInitials }}
            </div>
            <div class="public-payment-page__business">
              <span>Secure payment setup for</span>
              <h1>{{ Info.Business.name }}</h1>
              <a v-if="Info.Business.phoneNumber" :href="`tel:${Info.Business.phoneNumber}`">
                {{ Info.Business.phoneNumber }}
              </a>
            </div>
            <div class="public-payment-page__secure-badge">
              <v-icon size="16" color="success">mdi-lock-outline</v-icon>
              Secure
            </div>
          </header>

          <v-divider />

          <div v-if="Info" class="public-payment-page__content">
            <section class="public-payment-page__section" aria-labelledby="contact-heading">
              <div class="public-payment-page__section-heading">
                <span class="public-payment-page__step">1</span>
                <div>
                  <h2 id="contact-heading">Your information</h2>
                  <p>Confirm who this payment method belongs to.</p>
                </div>
              </div>
              <v-row class="public-payment-page__fields">
                <v-col cols="12" sm="6">
                <v-text-field
                  outlined
                  :error-messages="nameErrors"
                  :hide-details="nameErrors.length === 0"
                  dense
                  v-model="sponsor.name"
                  label="Full name"
                  autocomplete="name"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  outlined
                  @change="fixEmail"
                  :error-messages="emailErrors"
                  :hide-details="emailErrors.length === 0"
                  dense
                  v-model="sponsor.email"
                  label="Email Address"
                  type="email"
                  autocomplete="email"
                ></v-text-field>
              </v-col>
            </v-row>
            </section>

            <section class="public-payment-page__section" aria-labelledby="payment-heading">
              <div class="public-payment-page__section-heading">
                <span class="public-payment-page__step">2</span>
                <div>
                  <h2 id="payment-heading">Payment method</h2>
                  <p>{{ paymentMethodDescription }}</p>
                </div>
              </div>
              <CreditCard
                @submit="paymentCollected"
                :sponsor="sponsor"
                :donationCategoryId="paymentCategory.id"
                :paymentToken="$route.params.id"
                :hideBank="isStripe"
                :customer="Info"
                :public="true"
              />
            </section>

            <section class="public-payment-page__authorization">
              <v-checkbox
                v-model="authorizationAccepted"
                :rules="[(v) => !!v || 'You must authorize this payment']"
                color="primary"
                hide-details
                required
              >
                <template v-slot:label>
                  <span>
                    {{ authorizationText }}
                    <button type="button" class="public-payment-page__terms-button" @click.stop="showTerms = !showTerms">
                      {{ showTerms ? "Hide details" : "View authorization details" }}
                    </button>
                  </span>
                </template>
              </v-checkbox>
              <v-expand-transition>
                <p v-if="showTerms" class="public-payment-page__terms">
                  {{ authorizationDetails }}
                </p>
              </v-expand-transition>
            </section>

            <v-btn
              @click="submit"
              :loading="loading"
              :disabled="!authorizationAccepted"
              color="primary"
              depressed
              block
              x-large
              class="public-payment-page__submit"
              >Save payment method</v-btn
            >
          </div>
        </v-card>

        <v-card v-else class="public-payment-page__state-card" outlined>
          <lottie
            :height="200"
            :options="defaultOptions"
            name="receiptCheck"
            v-on:animCreated="handleAnimation"
            @onComplete="done"
          />
          <div v-if="showValue" class="text-center">
            <h1>Payment method saved</h1>
            <p>You're all set. You can safely close this page.</p>
          </div>
        </v-card>
      </template>

      <footer class="public-payment-page__footer">
        <v-icon size="15">mdi-lock-outline</v-icon>
        Secured by taleem.app <span aria-hidden="true">•</span> v{{ version }}
      </footer>
    </main>
  </div>
</template>
<!-- <script src="https://js.stripe.com/v3/"></script> -->
<script>
import customerService from "@/modules/Customer/service.js";
// import CreditCard from "@/modules/Donors/PaymentInfoCollect/CreditCard.vue";
// import { Card, createToken } from "vue-stripe-elements-plus";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/1708-success.json";
const pjson = require("../../../../package.json");
const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

import applicationService from "@/modules/Application/service.js";
import paymentMethodService from "@/modules/PaymentMethod/service.js";

import CreditCard from "@/modules/PaymentMethod/components/CreditCard.vue";

export default {
  name: "card-collect",
  data() {
    return {
      authorizationAccepted: false,
      showTerms: false,
      CustomerId: null,
      sponsor: {
        name: null,
        email: null,
      },
      loading: false,
      notFound: false,
      showValue: false,
      cardCollected: false,
      Info: null,
      complete: false,
      stripe: null,
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
    };
  },
  components: {
    CreditCard,
    Lottie,
  },
  created() {
    this.loadData();
  },
  mixins: [validationMixin],
  validations: {
    sponsor: {
      name: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    businessInitials() {
      if (!this.Info || !this.Info.Business || !this.Info.Business.name) return "T";
      return this.Info.Business.name
        .split(" ")
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase();
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.sponsor.email.$dirty) return errors;
      if (!this.$v.sponsor.email.required)
        errors.push("Email address is required");
      if (!this.$v.sponsor.email.email) errors.push("Email is not valid");
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.sponsor.name.$dirty) return errors;
      if (!this.$v.sponsor.name.required) errors.push("Name is required");
      return errors;
    },
    version() {
      return pjson.version;
    },
    paymentCategory() {
      return this.Info && this.Info.PaymentCollectionCategory
        ? this.Info.PaymentCollectionCategory
        : { id: this.Info && this.Info.Business ? this.Info.Business.DefaultSchoolFeeId : null };
    },
    isStripe() {
      return !!(this.paymentCategory.MerchantAccount && this.paymentCategory.MerchantAccount.provider === "STRIPE");
    },
    paymentMethodDescription() {
      return this.isStripe ? "Add a debit or credit card." : "Choose a debit or credit card, or use a bank account.";
    },
    authorizationText() {
      return this.isStripe
        ? `I authorize ${this.Info.Business.name} to charge the card provided in the amount and frequency agreed upon.`
        : `I authorize ${this.Info.Business.name} to initiate recurring debits from the account or card provided, in the amount and frequency agreed upon.`;
    },
    authorizationDetails() {
      return this.isStripe
        ? "I understand that I may revoke this authorization by providing written notice to the organization."
        : "I understand that I may revoke this authorization by providing written notice and that I have recourse rights if a debit does not comply with this agreement. For more information, visit nacha.org.";
    },
  },
  methods: {
    fixEmail() {
      if (this.sponsor.email) {
        this.sponsor.email = this.sponsor.email.toLowerCase().trim();
      }
    },
    done() {
      this.showValue = true;
    },
    paymentCollected() {
      this.cardCollected = true;
    },
    submit() {
      this.$events.emit("submitCreditCard");
    },
    handleAnimation(anim) {
      this.anim = anim;
    },
    async submitStripe() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        let data = {};
        // data = await createToken();
        // data.email = this.email;
        // data.name = this.name;
        console.log("data", data);
        customerService
          .PaymentInfoMarkCollected(this.Info.id, {
            customer: this.Info,
            processingType: this.PaymentMethod.processingType,
            token: data,
            name: this.name,
            email: this.email,
          })
          .then((response) => {
            console.log("response", response);
            this.cardCollected = true;
          })
          .catch((err) => {
            this.loading = false;
            console.log("error", err);
            this.$swal("Card Update", err.data.message, "error");
            // this.$swal
            //   .mixin({
            //     toast: true,
            //     position: "top-end",
            //     showConfirmButton: false,
            //     timer: 3000,
            //   })
            //   .fire({
            //     icon: "error",
            //     title: err.data.message
            //   });
          });
      }
      // console.log("val", token, this.Info);
    },
    async submit1() {
      this.$v.sponsor.$touch();
      if (this.paymentType === "card") {
        this.$v.card.$touch();
        if (this.$v.card.$invalid) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: this.$t("toastMessage.text.invalidFields"),
            });
          return false;
        }
      }

      try {
        this.loading = true;

        let data = {};

        data.accountType = this.accountType;
        data.paymentType = this.paymentType;
        data.sponsor = this.sponsor;
        (data.BusinessId = this.Info.BusinessId),
          (data.CustomerId = this.CustomerId);

        let card = null;
        if (this.paymentType === "card") {
          const expiryDate = this.card.expiryDate.split("/");
          card = {
            name: this.card.nameOnCard,
            number: this.card.cardNumber.replace(/\s/g, ""),
            exp_month: expiryDate[0],
            exp_year: `20${expiryDate[1]}`,
            cvc: this.card.cvvCode,
          };
        } else {
          card = {
            name: this.paymentMethod.accountName,
            routing_number: this.paymentMethod.routingNumber,
            account_type: data.accountType,
            sec_code: "PPD",
            account_number: this.paymentMethod.accountNumber,
          };
        }

        // data.card = card
        data.card = encryptor.encrypt(JSON.stringify(card));
        applicationService
          .publicAddCard(data)
          .then(() => {
            this.cardCollected = true;
          })
          .catch((error) => {
            this.loading = false;
            console.log("error adding card", error);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: error.data.message,
              });
          });
      } catch (error) {
        this.loading = false;
        console.log("error adding card", error);
      }
    },
    async submitOld() {
      this.$v.sponsor.$touch();
      this.$v.card.$touch();
      if (this.$v.card.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return false;
      }

      try {
        this.loading = true;
        let PaymentMethodEncrypted = null;
        PaymentMethodEncrypted = {
          processingType: "card",
          accountType: this.accountType,
          CustomerId: this.Info.id,
        };

        if (this.card.nameOnCard)
          PaymentMethodEncrypted.name = encryptor.encrypt(this.card.nameOnCard);
        if (this.card.cardNumber)
          PaymentMethodEncrypted.cardNumber = encryptor.encrypt(
            this.card.cardNumber
          );
        if (this.card.expiryDate)
          PaymentMethodEncrypted.expirationDate = encryptor.encrypt(
            this.card.expiryDate
          );
        if (this.card.cvvCode)
          PaymentMethodEncrypted.cvvCode = encryptor.encrypt(this.card.cvvCode);
        // if (this.paymentMethod.cardType)
        PaymentMethodEncrypted.cardType = encryptor.encrypt("local");
        // if (this.paymentMethod.zipCode)
        //   PaymentMethodEncrypted.zipCode = encryptor.encrypt(
        //     this.paymentMethod.zipCode
        //   );

        let data = {};
        // data = await createToken();

        data.accountType = this.accountType;
        data.paymentType = this.paymentType;
        data.sponsor = this.sponsor;

        // data.PaymentMethodEncrypted = PaymentMethodEncrypted
        console.log("data", PaymentMethodEncrypted);
        // customerService
        //   .PaymentInfoMarkCollected(this.Info.id, data)
        //   .then((response) => {
        //     console.log("response", response);
        //     this.cardCollected = true;
        //   });

        const expiryDate = this.card.expiryDate.split("/");
        let card = {
          name: this.card.nameOnCard,
          number: this.card.cardNumber.replace(/\s/g, ""),
          exp_month: expiryDate[0],
          exp_year: `20${expiryDate[1]}`,
          cvc: this.card.cvvCode,
          BusinessId: this.Info.BusinessId,
        };

        applicationService
          .publicCreateStripeToken(card)
          .then((response) => {
            console.log("test", response);
            data.token = response.data.id;
            customerService
              .PaymentInfoMarkCollected(this.Info.id, data)
              .then((response) => {
                console.log("response", response);

                return paymentMethodService
                  .create(PaymentMethodEncrypted)
                  .then((result) => {
                    console.log("result", result);
                    this.cardCollected = true;
                    // if (result.status) {
                    //   this.$swal
                    //     .mixin({
                    //       toast: true,
                    //       position: "top-end",
                    //       showConfirmButton: false,
                    //       timer: 3000,
                    //     })
                    //     .fire({
                    //       icon: "success",
                    //       title: "Payment Method is saved",
                    //     });
                    //   this.loading = false;
                    //   this.$emit("submit");
                    //   this.close();
                    // }
                  });
              })
              .catch((error) => {
                this.loading = false;
                console.log("error adding card", error);
              });
          })
          .catch((error) => {
            this.loading = false;
            console.log("error adding card", error);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: error.data.message,
              });
          });
      } catch (error) {
        this.loading = false;
        console.log("error adding card", error);
      }
    },
    loadData() {
      let id = this.$route.params.id;

      return customerService
        .getPaymentReqById(id)
        .then((response) => {
          console.log("response new", id, response);
          if (response.data && response.data.PaymentCollectionCategory) {
            this.Info = response.data;
            this.CustomerId = this.Info.id;
            this.sponsor.name = this.Info.fatherName;
            this.sponsor.email = this.Info.fatherEmailAddress;
            // this.StripeKey = this.Info.Business.metadata.publicStripeId;
            // this.cardCollected = this.Info.isCollected;
            document.title = this.Info.Business.name;
          } else {
            // console.log("asdkfjaslkdfjlaksjdf;lkasjdf");
            this.notFound = true;
          }

          // this.Donor = response.data;
          // console.log('response.data.alert', response)
          // if (response.data && !response.data.alert) response.data.alert = false
          this.$Progress.finish();
        })
        .catch(() => {
          this.loading = false;
          this.$Progress.finish();
        });
    },
  },
};
</script>

<style scoped>
.public-payment-page {
  background: #f4f7f5;
  color: #1f2937;
  min-height: 100vh;
  padding: 32px 20px;
}

.public-payment-page__shell {
  margin: 0 auto;
  max-width: 680px;
}

.public-payment-page__card,
.public-payment-page__state-card {
  border-color: #dde5df;
  border-radius: 18px;
  box-shadow: 0 12px 36px rgba(30, 48, 37, 0.08);
  overflow: hidden;
}

.public-payment-page__header {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: auto 1fr auto;
  padding: 24px 28px;
}

.public-payment-page__logo {
  border-radius: 10px;
  height: 52px;
  object-fit: contain;
  width: 52px;
}

.public-payment-page__logo-placeholder {
  align-items: center;
  background: #e8f4ea;
  border-radius: 12px;
  color: var(--v-primary-base);
  display: flex;
  font-size: 18px;
  font-weight: 700;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.public-payment-page__business span {
  color: #667085;
  font-size: 12px;
}

.public-payment-page__business h1 {
  font-size: 20px;
  line-height: 1.3;
  margin: 1px 0;
}

.public-payment-page__business a {
  font-size: 13px;
  text-decoration: none;
}

.public-payment-page__secure-badge {
  align-items: center;
  background: #edf8ef;
  border-radius: 20px;
  color: #2e7d32;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;
  padding: 6px 10px;
}

.public-payment-page__content {
  padding: 28px;
}

.public-payment-page__section + .public-payment-page__section {
  border-top: 1px solid #eaeeeb;
  margin-top: 26px;
  padding-top: 26px;
}

.public-payment-page__section-heading {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}

.public-payment-page__step {
  align-items: center;
  background: #e8f4ea;
  border-radius: 50%;
  color: var(--v-primary-base);
  display: flex;
  flex: 0 0 28px;
  font-size: 13px;
  font-weight: 700;
  height: 28px;
  justify-content: center;
}

.public-payment-page__section-heading h2 {
  font-size: 17px;
  line-height: 1.3;
  margin: 2px 0 3px;
}

.public-payment-page__section-heading p {
  color: #667085;
  font-size: 13px;
  margin: 0;
}

.public-payment-page__fields {
  margin-bottom: -12px;
}

.public-payment-page__authorization {
  background: #f8faf8;
  border: 1px solid #e2e8e3;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  margin: 28px 0 18px;
  padding: 8px 14px 12px;
}

.public-payment-page__terms-button {
  color: var(--v-primary-base);
  display: block;
  font-weight: 600;
  margin-top: 3px;
}

.public-payment-page__terms-button:focus {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

.public-payment-page__terms {
  color: #667085;
  margin: 8px 0 0 34px;
}

.public-payment-page__submit {
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  min-height: 52px;
}

.public-payment-page__footer {
  align-items: center;
  color: #7a857d;
  display: flex;
  font-size: 12px;
  gap: 5px;
  justify-content: center;
  padding: 18px;
}

.public-payment-page__state-card {
  padding: 48px 28px;
  text-align: center;
}

.public-payment-page__state-card h1 {
  font-size: 22px;
  margin: 14px 0 6px;
}

.public-payment-page__state-card p {
  color: #667085;
  margin: 0;
}

@media (max-width: 600px) {
  .public-payment-page {
    padding: 0;
  }

  .public-payment-page__card {
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .public-payment-page__header {
    gap: 10px;
    padding: 18px 16px;
  }

  .public-payment-page__logo,
  .public-payment-page__logo-placeholder {
    height: 44px;
    width: 44px;
  }

  .public-payment-page__business h1 {
    font-size: 17px;
  }

  .public-payment-page__secure-badge {
    padding: 5px 7px;
  }

  .public-payment-page__content {
    padding: 24px 16px 12px;
  }

  .public-payment-page__section-heading {
    margin-bottom: 14px;
  }

  .public-payment-page__authorization {
    margin-top: 22px;
  }

  .public-payment-page__footer {
    background: white;
  }
}
</style>
